import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import selfServicesApi from 'modules/selfServices/service';
import { messageUpdateReaction, newIncomingMessage } from 'modules/sockets/actions';

import { IncomingMessage } from 'modules/sockets/types/SocketResponse';

import chatApi from './service';
import { messagesAdapter } from './adapters';

import type { Message } from './types/Message';
import type InitialState from './types/InitialState';

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    page: undefined,
    messages: messagesAdapter.getInitialState(),
    unreadCount: 0,
  } as InitialState,
  reducers: {
    setPageNumber: (state, action: PayloadAction<number>) => ({
      ...state,
      page: {
        ...state.page,
        number: action.payload,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      chatApi.endpoints.getMessages.matchFulfilled,
      (state, action) => ({
        page: action.payload.page,
        messages: (action.payload.page.number === 0
          ? messagesAdapter.setAll({ ...state.messages }, action.payload.content)
          : messagesAdapter.upsertMany({ ...state.messages }, action.payload.content)),
      }),
    );

    const prependMessage = (
      state: InitialState,
      action: PayloadAction<Message | IncomingMessage>,
    ) => {
      let newMessage = action.payload as Message;
      if ('data' in action.payload) {
        newMessage = action.payload.data.message;
      }

      const messages = messagesAdapter.addOne(
        { ...state.messages },
        newMessage,
      );

      // Move the new message to the top
      const ids = [...messages.ids];
      ids.pop();
      ids.unshift(newMessage.id);
      messages.ids = ids;

      return {
        ...state,
        messages,
      };
    };

    builder.addMatcher(newIncomingMessage.match, prependMessage);
    builder.addMatcher(messageUpdateReaction.match, (state, action) => ({
      ...state,
      messages: messagesAdapter.setOne({ ...state.messages }, action.payload.data.message)
    }));

    builder.addMatcher(chatApi.endpoints.readMessages.matchFulfilled, (state) => ({
      ...state,
      messages: messagesAdapter.updateMany(
        { ...state.messages },
        state.messages.ids.map((id) => ({
          id,
          changes: { unread: false },
        })),
      ),
    }));

    builder.addMatcher(selfServicesApi.endpoints.getFinalInstructions.matchFulfilled, (state, { payload }) => ({
      ...state,
      unreadCount: payload.unreadCount,
    }));

    builder.addMatcher(chatApi.endpoints.getMessages.matchFulfilled, (state, { payload }) => ({
      ...state,
      unreadCount: payload.content.reduce((acc, { unread }) => (unread ? acc + 1 : acc), 0),
    }));
  },
});

export default chatSlice;
